import { DELETE, GET, PATCH, POST } from "../config/axios.config";
import { normalizeUser } from "./normalizers";

/**
 * v2 identity is a Bangladeshi phone number, always. There is no e-mail login
 * and no separate "verify login" step: registration issues an OTP, verifying it
 * returns the session token, and login is phone + password in one call.
 */

/** 01712345678 / 8801712345678 / +8801712345678 → +8801712345678 */
export const toBdPhone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("880")) digits = digits.slice(3);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return `+880${digits}`;
};

export const isValidBdPhone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("880")) digits = digits.slice(3);
  if (digits.startsWith("0")) digits = digits.slice(1);
  return /^1[3-9]\d{8}$/.test(digits);
};

export const persistSession = ({ user, token }) => {
  const normalized = normalizeUser(user);
  if (token) {
    localStorage.setItem("Token", token);
    localStorage.setItem("Token2", JSON.stringify(token));
  }
  if (normalized) {
    localStorage.setItem("UserDetails", JSON.stringify(normalized));
  }
  return normalized;
};

export const clearSession = () => {
  localStorage.removeItem("Token");
  localStorage.removeItem("Token2");
  localStorage.removeItem("UserDetails");
};

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("UserDetails"));
  } catch {
    return null;
  }
};

export const isAuthenticated = () =>
  !!localStorage.getItem("Token") && !!getStoredUser()?._id;

/* ── Endpoints ────────────────────────────────────────────────────────── */

export const register = ({ name, phone, password, email, referencePhone }) =>
  POST("/auth/register", {
    name,
    phone: toBdPhone(phone),
    password,
    ...(email ? { email } : {}),
    ...(referencePhone ? { referencePhone: toBdPhone(referencePhone) } : {}),
  });

/** Verifying the registration OTP is what logs the user in. */
export const verifyRegistrationOtp = async ({ phone, otp }) => {
  const res = await POST("/auth/verify-registration-otp", {
    phone: toBdPhone(phone),
    otp,
  });
  persistSession(res?.data || {});
  return res;
};

export const resendOtp = ({ phone }) =>
  POST("/auth/resend-otp", { phone: toBdPhone(phone) });

export const login = async ({ phone, password }) => {
  const res = await POST("/auth/login", { phone: toBdPhone(phone), password });
  persistSession(res?.data || {});
  return res;
};

export const forgotPassword = ({ phone }) =>
  POST("/auth/forgot-password", { phone: toBdPhone(phone) });

/**
 * v2 resets in a single call — the OTP is submitted together with the new
 * password, so there is no intermediate "verify OTP → reset token" round trip.
 */
export const resetPassword = ({ phone, otp, password }) =>
  POST("/auth/reset-password", { phone: toBdPhone(phone), otp, password });

export const fetchMe = async () => {
  const res = await GET("/auth/me");
  const user = normalizeUser(res?.data?.user);
  if (user) localStorage.setItem("UserDetails", JSON.stringify(user));
  return user;
};

export const updateMe = async (payload) => {
  const res = await PATCH("/auth/me", payload);
  const user = normalizeUser(res?.data?.user);
  if (user) localStorage.setItem("UserDetails", JSON.stringify(user));
  return user;
};

export const deleteMe = async ({ password, reason }) => {
  const res = await DELETE("/auth/me", {
    data: { password, ...(reason ? { reason } : {}) },
  });
  clearSession();
  return res;
};
